import React, { useState } from 'react';

import type { UnitDTO, LessonDTO } from '../StudentDashboardDTOs';
import { convertToCdnUrl } from '@/src/utils';
import lessonVideoThumbnail from '@/src/assets/thumbnails/video-thumbnail-lesson.png';
import strategyVideoThumbnail from '@/src/assets/thumbnails/video-thumbnail-strategy.png';

const VideoPreview: React.FC<{ link: string; type: 'Lesson' | 'Strategy' }> = ({ link, type }) => {
    const [hasError, setHasError] = useState(false);

    const thumbSrc = type === 'Lesson' ? lessonVideoThumbnail : strategyVideoThumbnail;

    if (hasError) return null;

    return (
        <div className="video-container">
            <div className="video-label">{type} Video</div>
            <img
                src={thumbSrc}
                className="video-thumbnail-overlay"
                alt={`${type} Video Thumbnail`}
                onError={(e) => {
                    e.currentTarget.style.display = 'none';
                }}
            />
            <video
                className="preview-video"
                muted
                playsInline
                preload="metadata"
                onError={() => setHasError(true)}
                onMouseOver={(e) => e.currentTarget.play().catch(() => {})}
                onMouseOut={(e) => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                }}
                onClick={() => window.open(link, '_blank')}
            >
                <source
                    src={`${convertToCdnUrl(link)}`}
                    type="video/mp4"
                    onError={() => setHasError(true)}
                />
                Your browser does not support the video tag.
            </video>
        </div>
    );
};

const LessonAccordion: React.FC<{ lesson: LessonDTO }> = ({ lesson }) => {
    const [isOpen, setIsOpen] = useState(false);

    const lessonLink =
        lesson.subitems.find((item) => item.type.toLowerCase().includes('lesson'))?.link || '';

    const strategyLink =
        lesson.subitems.find((item) => item.type.toLowerCase().includes('strategy'))?.link || '';

    const hasVideos = lessonLink || strategyLink;

    return (
        <div className="lesson-wrapper">
            <div
                className={`lesson-header ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="lesson-arrow">{isOpen ? '▼' : '▶'}</span>
                <span>
                    Lesson {lesson.lessonNumber}: {lesson.title}
                </span>
            </div>

            <div className={`lesson-content-wrapper ${isOpen ? 'open' : ''}`}>
                {!hasVideos && (
                    <div className="no-videos-msg">No videos available for this lesson.</div>
                )}

                <div className="video-row">
                    {/* Render simply based on existence of the string, same as Teacher code */}
                    {lessonLink ? <VideoPreview link={lessonLink} type="Lesson" /> : null}
                    {strategyLink ? <VideoPreview link={strategyLink} type="Strategy" /> : null}
                </div>
            </div>
        </div>
    );
};

const UnitAccordion: React.FC<{ unit: UnitDTO }> = ({ unit }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="unit-wrapper">
            <div className="unit" onClick={() => setIsOpen(!isOpen)}>
                <span>
                    Unit {unit.unitNumber}: {unit.unitTitle}
                </span>
                <span className={`unit-arrow ${isOpen ? 'rotated' : ''}`}>▼</span>
            </div>

            {isOpen && (
                <div className={`unit-content-wrapper open`}>
                    <div className="lesson-container">
                        {unit.lessons.map((lesson) => (
                            <LessonAccordion key={lesson.id} lesson={lesson} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UnitAccordion;
